import { client } from '../content/client';
import { pendingOf, textOf } from '../content/types';
import { BookMeeting } from '../islands/BookMeeting';
import { DemoVideoSection } from '../islands/DemoVideo';
import { CopyText, Pending } from '../lib/marks';
import s from '../site.module.css';
import { ICONS, IconBadge, InlineIcon } from '../visuals/icons';
import { DriftTimeline, HeldGroupIllustration, PortalOverviewIllustration, ShadowSummary } from '../visuals/Visuals';
import { CtaRow, Section, SiteLayout } from './layout';

/** The client page at /: what Pairing does for a team whose HubSpot sync breaks quietly. */
export function ClientPage() {
  const c = client;
  return (
    <SiteLayout>
      <section className={s.hero} aria-labelledby="hero-title">
        <div className={`${s.container} ${s.heroGrid}`}>
          <div className={s.heroCopy}>
            <p className={s.eyebrow}>{c.hero.eyebrow}</p>
            <h1 id="hero-title">{c.hero.headline}</h1>
            <p className={s.lede}>{c.hero.subhead}</p>
            <CtaRow primary={c.hero.primary} secondary={c.hero.secondary} onDark />
          </div>
          <DriftTimeline visual={c.hero.visual} />
        </div>
      </section>

      <Section tone="white" labelledBy="held-title">
        <div className={s.sectionHead}>
          <h2 id="held-title">{c.heldRecords.headline}</h2>
          <p className={s.lede}>{c.heldRecords.body}</p>
        </div>
        <ol className={s.steps}>
          {c.heldRecords.steps.map((step, i) => (
            <li key={step.title} className={s.stepCard}>
              <span className={s.stepIcon}>
                <IconBadge icon={ICONS.heldSteps[i]} />
              </span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
        <div className={s.split}>
          <p className={s.body}>{c.heldRecords.supporting}</p>
          <HeldGroupIllustration visual={c.heldRecords.visual} />
        </div>
      </Section>

      <DemoVideoSection />

      <Section labelledBy="shadow-title">
        <div className={`${s.split} ${s.splitWide}`}>
          <div className={s.stack}>
            <h2 id="shadow-title">{c.shadow.headline}</h2>
            <p className={s.body}>{c.shadow.body}</p>
            <ul className={s.checkList}>
              {c.shadow.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <ShadowSummary visual={c.shadow.visual} />
        </div>
      </Section>

      <Section tone="white" labelledBy="capabilities-title">
        <div className={s.sectionHead}>
          <h2 id="capabilities-title">{c.capabilities.headline}</h2>
        </div>
        <ul className={s.cards}>
          {c.capabilities.cards.map((card, i) => (
            <li key={card.title} className={s.card}>
              <IconBadge icon={ICONS.capabilities[i]} />
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section labelledBy="portal-title">
        <div className={s.sectionHead}>
          <h2 id="portal-title">{c.portal.headline}</h2>
          <CopyText copy={c.portal.body} className={s.lede} />
        </div>
        <PortalOverviewIllustration visual={c.portal.visual} />
        <ul className={s.proof} aria-label={c.portal.proofLabel}>
          {c.portal.proof.map((p, i) => (
            <li key={p}>
              <InlineIcon icon={ICONS.proof[i]} />
              {p}
            </li>
          ))}
        </ul>
        <Pending marker={pendingOf(c.portal.dataHeadline) ?? pendingOf(c.portal.dataBody)} className={s.dataBlock}>
          <h3 className={s.iconHeading}>
            <InlineIcon icon={ICONS.data} />
            {textOf(c.portal.dataHeadline)}
          </h3>
          <p className={s.body}>{textOf(c.portal.dataBody)}</p>
        </Pending>
      </Section>

      <Section tone="navy" labelledBy="elsewhere-title">
        <div className={s.sectionHead}>
          <h2 id="elsewhere-title">{c.notTheAnswer.headline}</h2>
          <p className={s.lede}>{c.notTheAnswer.body}</p>
        </div>
        <ul className={s.plainList}>
          {c.notTheAnswer.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Section>

      <Section tone="white" labelledBy="faq-title">
        <div className={s.sectionHead}>
          <h2 id="faq-title">{c.faq.headline}</h2>
        </div>
        <div className={s.faq}>
          {c.faq.items.map((item) => (
            <details key={item.q} className={s.faqItem}>
              <summary>
                <h3 className={s.faqQuestion}>{item.q}</h3>
              </summary>
              <CopyText copy={item.a} className={s.faqAnswer} />
            </details>
          ))}
        </div>
      </Section>

      <Section id={c.contact.id} tone="image" labelledBy="contact-title">
        <div className={s.contactGrid}>
          <div className={s.stack}>
            <h2 id="contact-title">{c.contact.headline}</h2>
            <p className={s.body}>{c.contact.body}</p>
          </div>
          <div className={s.stack}>
            <BookMeeting />
            <p className={s.small}>
              {c.contact.below.before}
              <a href={c.contact.below.href}>{c.contact.below.label}</a>
              {c.contact.below.after}
            </p>
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}
